import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from './components/button/button.component';
import { IconComponent } from './components/icon/icon.component';
import { CalloutComponent } from './components/callout/callout.component';
import { MatRippleModule } from '@angular/material/core';
import { ProgressSpinnerComponent } from './components/progress-spinner/progress-spinner.component';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollComponent } from './components/infinite-scroll/infinite-scroll.component';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { MaterialModule } from '../_material/material.module';
import { TooltipDirective } from './directives/tooltip/tooltip.directive';
import { SafePipe } from './pipes/safe/safe.pipe';
// New SwiperJS
import { SwiperComponent } from './components/swiper/swiper.component';
// import function to register Swiper custom elements
import { register } from 'swiper/element/bundle';
import { SwiperItemComponent } from './components/swiper-item/swiper-item.component';
import { DefaultCoverDirective } from './directives/default-cover.directive';
import { EllipsisDirective } from './directives/ellipsis.directive';
import { AvatarComponent } from './components/avatar/avatar.component';
import { AvatarTextPipe } from './pipes/avatar-text/avatar-text.pipe';
import { AvatarImagePipe } from './components/avatar/pipes/avatar-image.pipe';
import { TagComponent } from './components/tag/tag.component';
import { PopoverComponent } from './components/popover/popover.component';
import { PopoverTriggerDirective } from './components/popover/popover-trigger.directive';
import { RatingComponent } from './components/rating/rating.component';
import { RecordCoverComponent } from './components/record-cover/record-cover.component';
import { SkeletonComponent } from './components/skeleton/skeleton.component';
import { GradientComponent } from './components/gradient/gradient.component';
import { TextInputComponent } from './components/forms/text-input/text-input.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PasswordInputComponent } from './components/forms/password-input/password-input.component';
import { ComposableFieldComponent } from './components/forms/composable-field/composable-field.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { DataTestIdDirective } from './directives/data-test-id.directive';
import { ViewportObserverDirective } from './directives/viewport-observer.directive';
import { RecaptchaDialogComponent } from './components/dialogs/recaptcha-dialog/recaptcha-dialog.component';
import { RecaptchaModule } from 'ng-recaptcha';
import { TextareaComponent } from './components/forms/textarea/textarea.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { MenuComponent } from './components/menu/menu.component';
import { MenuItemComponent } from './components/menu-item/menu-item.component';
import { MenuTriggerDirective } from './components/menu/menu-trigger.directive';
import { TableComponent } from './components/table/table.component';
import { TableRowComponent } from './components/table-row/table-row.component';
import { TableCellComponent } from './components/table-cell/table-cell.component';
import { TableCellHeaderComponent } from './components/table-cell-header/table-cell-header.component';
import { TableCellTextComponent } from './components/table-cell-text/table-cell-text.component';
import { CheckboxComponent } from './components/forms/checkbox/checkbox.component';
import { CardItemComponent } from './components/card-item/card-item.component';
import { RouterModule } from '@angular/router';
import { StringToDatePipe } from './pipes/string-to-date/string-to-date.pipe';
import { CardItemDetailsComponent } from './components/card-item/components/card-item-details/card-item-details.component';
import { CardItemDetailComponent } from './components/card-item/components/card-item-detail/card-item-detail.component';
import { CardItemSocialComponent } from './components/card-item/components/card-item-social/card-item-social.component';
import { GoBackDirective } from './directives/go-back.directive';
import { SelectComponent } from './components/forms/select/select.component';
import { OptionComponent } from './components/forms/option/option.component';
import { SwitchComponent } from './components/forms/switch/switch.component';
import { AuthorCardItemComponent } from './components/author-card-item/author-card-item.component';
import { DividerComponent } from './components/divider/divider.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { CardItemDescriptionComponent } from './components/card-item/components/card-item-description/card-item-description.component';
import { CardItemSkeletonComponent } from './components/card-item-skeleton/card-item-skeleton.component';
import { InfoUrlPipe } from './pipes/info-url/info-url.pipe';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { RadioComponent } from './components/forms/radio/radio.component';
import { RadioGroupDirective } from './components/forms/radio/radio-group.directive';
import { NoResultsNotificationsComponent } from './components/no-results/images/no-results-notifications/no-results-notifications.component';
import { TabComponent } from './components/tab/tab.component';
import { TabGroupComponent } from './components/tab-group/tab-group.component';
import { TabPanelComponent } from './components/tab-panel/tab-panel.component';
import { BadgeComponent } from './components/badge/badge.component';
import { UserTitleComponent } from './components/user-title/user-title.component';
import { UploaderComponent } from './components/forms/uploader/uploader.component';
import { FilesizePipe } from './pipes/filesize/filesize.pipe';
import { JoinPipe } from './pipes/join/join.pipe';
import { MapPipe } from './pipes/map/map.pipe';
import { ChipComponent } from './components/chip/chip.component';
import { ConfirmationDialogComponent } from './components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { NoResultsCtaComponent } from './components/no-results-cta/no-results-cta.component';
import { InvalidControlPipe } from './pipes/invalid-control/invalid-control.pipe';
import { AutoFocusErrorDirective } from './directives/auto-focus-error.directive';
import { UserSectionComponent } from './components/user-section/user-section.component';
import { ToggleChipComponent } from './components/toggle-chip/toggle-chip.component';
import { RadioElementDirective } from './components/forms/radio/radio-element.directive';
import { NoResultsResultsComponent } from './components/no-results/images/no-results-results/no-results-results.component';
import { NoResultsRecommendComponent } from './components/no-results/images/no-results-recommend/no-results-recommend.component';
import { NoResultsEventsComponent } from './components/no-results/images/no-results-events/no-results-events.component';
import { NoResultsDefaultComponent } from './components/no-results/images/no-results-default/no-results-default.component';
import { FocusTrapDirective } from './directives/focus-trap.directive';
import { CitationDialogComponent } from './components/dialogs/citation-dialog/citation-dialog.component';
import { PublicationYearPipe } from './pipes/publication-year/publication-year.pipe';
import { ReportDialogComponent } from './components/dialogs/report-dialog/report-dialog.component';
import { ToastComponent } from './components/toast/toast.component';
import { ToasterComponent } from './components/toaster/toaster.component';
import { InfoLinkComponent } from './components/dialogs/bookclub-links-dialog/components/info-link/info-link.component';
import { ExternalVideoDialogComponent } from './components/dialogs/external-video-dialog/external-video-dialog.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { AccordionGroupComponent } from './components/accordion-group/accordion-group.component';
import { RatingDialogComponent } from './components/dialogs/rating-dialog/rating-dialog.component';
import { InfoDialogComponent } from './components/dialogs/info-dialog/info-dialog.component';
import { BookclubLinksDialogComponent } from './components/dialogs/bookclub-links-dialog/bookclub-links-dialog.component';
import { ResetPasswordDialogComponent } from './components/dialogs/reset-password-dialog/reset-password-dialog.component';
import { AmautasSubscriptionDialogComponent } from './components/dialogs/amautas-subscription-dialog/amautas-subscription-dialog.component';
import { CookiesDialogComponent } from './components/dialogs/cookies-dialog/cookies-dialog.component';
import { CookiesSettingsComponent } from './components/dialogs/cookies-dialog/cookies-settings/cookies-settings.component';
import { CookiesOverviewComponent } from './components/dialogs/cookies-dialog/cookies-overview/cookies-overview.component';
import { DurationPipe } from './pipes/duration/duration.pipe';
import { FilterGroupComponent } from './components/filter-group/filter-group.component';
import { FilterItemComponent } from './components/filter-group/components/filter-item/filter-item.component';
import { ExpandButtonComponent } from './components/filter-group/components/expand-button/expand-button.component';
import { TimeUntilPipe } from './pipes/time-until/time-until.pipe';

// register Swiper custom elements
register();

@NgModule({
  declarations: [
    ButtonComponent,
    IconComponent,
    ProgressSpinnerComponent,
    InfiniteScrollComponent,
    NoResultsComponent,
    SwiperComponent,
    SwiperItemComponent,
    TooltipDirective,
    SafePipe,
    DefaultCoverDirective,
    EllipsisDirective,
    AvatarComponent,
    AvatarTextPipe,
    AvatarImagePipe,
    TagComponent,
    PopoverComponent,
    PopoverTriggerDirective,
    RatingComponent,
    RecordCoverComponent,
    SkeletonComponent,
    GradientComponent,
    TextInputComponent,
    PasswordInputComponent,
    ComposableFieldComponent,
    ProgressBarComponent,
    DataTestIdDirective,
    ViewportObserverDirective,
    RecaptchaDialogComponent,
    TextareaComponent,
    DialogComponent,
    MenuComponent,
    MenuItemComponent,
    MenuTriggerDirective,
    TableComponent,
    TableRowComponent,
    TableCellComponent,
    TableCellHeaderComponent,
    TableCellTextComponent,
    CheckboxComponent,
    CardItemComponent,
    StringToDatePipe,
    CardItemDetailsComponent,
    CardItemDetailComponent,
    CardItemSocialComponent,
    GoBackDirective,
    SelectComponent,
    OptionComponent,
    SwitchComponent,
    AuthorCardItemComponent,
    DividerComponent,
    SearchBarComponent,
    CardItemDescriptionComponent,
    CardItemSkeletonComponent,
    InfoUrlPipe,
    PaginatorComponent,
    RadioComponent,
    RadioGroupDirective,
    NoResultsNotificationsComponent,
    NoResultsResultsComponent,
    NoResultsRecommendComponent,
    NoResultsEventsComponent,
    NoResultsDefaultComponent,
    TabComponent,
    TabGroupComponent,
    TabPanelComponent,
    BadgeComponent,
    UserTitleComponent,
    UploaderComponent,
    FilesizePipe,
    JoinPipe,
    MapPipe,
    ChipComponent,
    ConfirmationDialogComponent,
    NoResultsCtaComponent,
    InvalidControlPipe,
    AutoFocusErrorDirective,
    UserSectionComponent,
    ToggleChipComponent,
    RadioElementDirective,
    FocusTrapDirective,
    CitationDialogComponent,
    PublicationYearPipe,
    ReportDialogComponent,
    ToastComponent,
    ToasterComponent,
    InfoLinkComponent,
    ExternalVideoDialogComponent,
    InfoDialogComponent,
    AccordionComponent,
    AccordionGroupComponent,
    RatingDialogComponent,
    BookclubLinksDialogComponent,
    ResetPasswordDialogComponent,
    AmautasSubscriptionDialogComponent,
    CookiesDialogComponent,
    CookiesSettingsComponent,
    CookiesOverviewComponent,
    DurationPipe,
    FilterGroupComponent,
    FilterItemComponent,
    ExpandButtonComponent,
    TimeUntilPipe,
    CalloutComponent,
  ],
  exports: [
    // Global
    AvatarComponent,
    AvatarTextPipe,
    AvatarImagePipe,
    ButtonComponent,
    IconComponent,
    ProgressSpinnerComponent,
    InfiniteScrollComponent,
    NoResultsComponent,
    SwiperComponent,
    SwiperItemComponent,
    SafePipe,
    DefaultCoverDirective,
    TooltipDirective,
    EllipsisDirective,
    PopoverComponent,
    PopoverTriggerDirective,
    RatingComponent,
    PopoverTriggerDirective,
    TagComponent,
    RecordCoverComponent,
    SkeletonComponent,
    GradientComponent,
    TextInputComponent,
    PasswordInputComponent,
    ComposableFieldComponent,
    ProgressBarComponent,
    DataTestIdDirective,
    ViewportObserverDirective,
    RecaptchaDialogComponent,
    TextareaComponent,
    DialogComponent,
    MenuComponent,
    MenuItemComponent,
    MenuTriggerDirective,
    TableComponent,
    TableRowComponent,
    TableCellComponent,
    TableCellHeaderComponent,
    TableCellTextComponent,
    CheckboxComponent,
    CardItemComponent,
    StringToDatePipe,
    CardItemDetailsComponent,
    CardItemDetailComponent,
    CardItemSocialComponent,
    GoBackDirective,
    SelectComponent,
    OptionComponent,
    SwitchComponent,
    AuthorCardItemComponent,
    DividerComponent,
    SearchBarComponent,
    CardItemDescriptionComponent,
    CardItemSkeletonComponent,
    InfoUrlPipe,
    PaginatorComponent,
    RadioComponent,
    RadioGroupDirective,
    NoResultsNotificationsComponent,
    NoResultsResultsComponent,
    NoResultsRecommendComponent,
    NoResultsEventsComponent,
    TabGroupComponent,
    TabComponent,
    TabPanelComponent,
    UserTitleComponent,
    UploaderComponent,
    ChipComponent,
    ConfirmationDialogComponent,
    MapPipe,
    NoResultsCtaComponent,
    InvalidControlPipe,
    AutoFocusErrorDirective,
    UserSectionComponent,
    ToggleChipComponent,
    RadioElementDirective,
    CitationDialogComponent,
    PublicationYearPipe,
    ReportDialogComponent,
    ToasterComponent,
    ExternalVideoDialogComponent,
    InfoDialogComponent,
    AccordionGroupComponent,
    AccordionComponent,
    BookclubLinksDialogComponent,
    RatingDialogComponent,
    ResetPasswordDialogComponent,
    AmautasSubscriptionDialogComponent,
    CookiesDialogComponent,
    DurationPipe,
    BadgeComponent,
    FilterGroupComponent,
    FilterItemComponent,
    ExpandButtonComponent,
    TimeUntilPipe,
    CalloutComponent,
  ],
  imports: [
    CommonModule,
    MatRippleModule,
    TranslateModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    RecaptchaModule,
    RouterModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
